import React, { useEffect, useMemo, useState } from 'react';
import { catalogueAPI, customerPurchaseOrdersAPI, Product, ProductDocument } from '../../services/api';
import { ArchiveBoxIcon, ArrowDownTrayIcon, DocumentIcon, MapPinIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Badge, Card, CardBody, Spinner } from '../ui';
import {
  flattenProductDocuments,
  getProductDocumentUrl,
  getProductDocumentParentOptions,
  getProductDocumentTypeLabel,
  getProductDocumentTypeVariant,
} from '../../utils/productDocuments';
import ProductQuoteHistoryPanel from './ProductQuoteHistoryPanel';

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
  allowDocumentManagement?: boolean;
}

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  onClose,
  onEdit,
  readOnly = false,
  allowDocumentManagement = !readOnly,
}) => {
  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [savingDocument, setSavingDocument] = useState(false);
  const [documentEditForm, setDocumentEditForm] = useState({
    fileName: '',
    fileType: 'COA' as 'COA' | 'MSDS' | 'OTHER',
  });
  const [uploadData, setUploadData] = useState({
    file: null as File | null,
    fileType: 'COA' as 'COA' | 'MSDS' | 'OTHER',
    fileName: '',
    parentDocumentId: '',
  });

  useEffect(() => {
    loadDocuments();
    setEditingDocumentId(null);
    setShowUploadForm(false);
  }, [product.id]);

  const allDocuments = useMemo(
    () => flattenProductDocuments(documents),
    [documents],
  );
  const parentOptions = useMemo(
    () => getProductDocumentParentOptions(documents),
    [documents],
  );

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docs = await catalogueAPI.getDocuments(product.id);
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadData((current) => ({
        ...current,
        file,
        fileName: current.fileName || file.name,
      }));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadData.file) return;

    try {
      setUploadingDoc(true);
      await catalogueAPI.uploadDocument(
        product.id,
        uploadData.file,
        uploadData.fileType,
        uploadData.fileName,
        uploadData.parentDocumentId ? Number(uploadData.parentDocumentId) : undefined,
      );
      setUploadData({ file: null, fileType: 'COA', fileName: '', parentDocumentId: '' });
      setShowUploadForm(false);
      loadDocuments();
    } catch (error: any) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (!window.confirm('Are you sure you want to delete this document and its child documents?')) return;

    try {
      await catalogueAPI.deleteDocument(docId);
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const beginEditDocument = (document: ProductDocument) => {
    setEditingDocumentId(document.id);
    setDocumentEditForm({
      fileName: document.fileName || document.originalName,
      fileType: document.fileType === 'MSDS' || document.fileType === 'OTHER' ? document.fileType : 'COA',
    });
  };

  const handleUpdateDocument = async () => {
    if (!editingDocumentId) return;

    try {
      setSavingDocument(true);
      await catalogueAPI.updateDocument(editingDocumentId, {
        fileName: documentEditForm.fileName.trim(),
        fileType: documentEditForm.fileType,
      });
      setEditingDocumentId(null);
      await loadDocuments();
    } catch (error: any) {
      console.error('Error updating document:', error);
      alert(error.response?.data?.message || 'Failed to update document');
    } finally {
      setSavingDocument(false);
    }
  };

  const renderDocuments = (items: ProductDocument[], depth = 0): React.ReactNode =>
    items.map((doc) => {
      const documentName = doc.fileName || doc.originalName || 'Document';

      return (
        <div key={doc.id} className="space-y-2">
          <Card variant="bordered">
            <CardBody className="py-3">
              <div className="space-y-3" style={{ paddingLeft: `${depth * 20}px` }}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-start">
                    <DocumentIcon className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">{documentName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Badge variant={getProductDocumentTypeVariant(doc.fileType)} size="sm">
                          {getProductDocumentTypeLabel(doc.fileType)}
                        </Badge>
                        <span className="text-xs text-neutral-500">{formatFileSize(doc.fileSize)}</span>
                        {depth > 0 && (
                          <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700">
                            Child document
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={getProductDocumentUrl(doc)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
                    >
                      Open
                    </a>
                    <a
                      href={getProductDocumentUrl(doc)}
                      download={documentName}
                      className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50"
                    >
                      <ArrowDownTrayIcon className="mr-1 h-4 w-4" />
                      Download
                    </a>
                    {allowDocumentManagement && (
                      <>
                        <button
                          type="button"
                          onClick={() => beginEditDocument(doc)}
                          className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100"
                          title="Edit document"
                          aria-label={`Edit document ${documentName}`}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="inline-flex items-center rounded-md border border-error-200 bg-white px-2.5 py-1.5 text-error-600 transition-colors hover:bg-error-50"
                          title="Delete document"
                          aria-label={`Delete document ${documentName}`}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {allowDocumentManagement && editingDocumentId === doc.id && (
                  <div className="grid grid-cols-1 gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3 sm:grid-cols-3">
                    <div>
                      <label className="block text-xs font-medium text-neutral-700">Document Type</label>
                      <select
                        value={documentEditForm.fileType}
                        onChange={(event) =>
                          setDocumentEditForm((current) => ({
                            ...current,
                            fileType: event.target.value as 'COA' | 'MSDS' | 'OTHER',
                          }))
                        }
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="COA">COA</option>
                        <option value="MSDS">MSDS</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-neutral-700">Display Name</label>
                      <input
                        aria-label="Document display name"
                        value={documentEditForm.fileName}
                        onChange={(event) => setDocumentEditForm((current) => ({ ...current, fileName: event.target.value }))}
                        className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                      <Button variant="primary" size="sm" onClick={handleUpdateDocument} isLoading={savingDocument}>
                        Save Document
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingDocumentId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {doc.childDocuments && doc.childDocuments.length > 0 && renderDocuments(doc.childDocuments, depth + 1)}
        </div>
      );
    });

  return (
    <Modal isOpen={true} onClose={onClose} title="Product Details" size="xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div></div>
          {!readOnly && onEdit && (
            <Button variant="primary" onClick={onEdit}>
              Edit Product
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h4 className="text-sm font-medium text-neutral-500">Product Name</h4>
            <p className="mt-1 text-sm text-neutral-900">{product.name}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-neutral-500">SKU</h4>
            <p className="mt-1 text-sm text-neutral-900">{product.sku}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-neutral-500">Category</h4>
            <p className="mt-1 text-sm text-neutral-900">{product.category?.name || 'No category'}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-neutral-500">Unit</h4>
            <p className="mt-1 text-sm text-neutral-900">{product.unit || 'Not specified'}</p>
          </div>

          {product.description && (
            <div className="md:col-span-2">
              <h4 className="text-sm font-medium text-neutral-500">Description</h4>
              <p className="mt-1 text-sm text-neutral-900">{product.description}</p>
            </div>
          )}
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-medium text-neutral-900">Lots & Stock</h4>
            <Badge variant={product.stockSummary?.isInventoryLinked === false ? 'neutral' : 'primary'} size="sm">
              {product.stockSummary?.isInventoryLinked === false ? 'Not Tracked' : `${product.stockSummary?.availableQuantity ?? 0} Available`}
            </Badge>
          </div>
          {product.lotSummaries && product.lotSummaries.length > 0 ? (
            <Card variant="bordered">
              <CardBody className="p-0">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Lot</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Available</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Expiry</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 bg-white">
                    {product.lotSummaries.map((lot) => (
                      <tr key={`${lot.lotId || lot.lotNumber}`}>
                        <td className="px-3 py-2 text-sm font-medium text-neutral-900">
                          <span className="inline-flex items-center gap-1">
                            <ArchiveBoxIcon className="h-4 w-4 text-neutral-400" />
                            {lot.lotNumber}
                          </span>
                          {lot.supplier?.name && <div className="mt-1 text-xs text-neutral-500">{lot.supplier.name}</div>}
                        </td>
                        <td className="px-3 py-2 text-sm text-neutral-700">{lot.availableQuantity} / {lot.totalQuantity}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700">{formatDate(lot.expiryDate)}</td>
                        <td className="px-3 py-2 text-sm text-neutral-700">
                          <span className="inline-flex items-center gap-1">
                            <MapPinIcon className="h-4 w-4 text-neutral-400" />
                            {lot.locations?.join(', ') || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          ) : (
            <p className="text-sm text-neutral-500">No lot-backed inventory is available for this product.</p>
          )}
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-medium text-neutral-900">Customer Purchase Orders</h4>
            <Badge variant="neutral" size="sm">{product.customerPurchaseOrders?.length || 0}</Badge>
          </div>
          {product.customerPurchaseOrders && product.customerPurchaseOrders.length > 0 ? (
            <div className="space-y-2">
              {product.customerPurchaseOrders.map((po) => (
                <Card key={po.id} variant="bordered">
                  <CardBody className="py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-neutral-900">{po.name}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                          <span>{po.deal?.client?.name || 'No client'}</span>
                          <span>{po.deal?.orderNumber || `Deal #${po.dealId}`}</span>
                          <span>{formatFileSize(po.fileSize)}</span>
                          <span>{formatDate(po.uploadedAt)}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => customerPurchaseOrdersAPI.download(po)}
                        leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
                      >
                        Download
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-500">No customer POs are linked to this product yet.</p>
          )}
        </div>

        <div>
          <h4 className="mb-3 text-sm font-medium text-neutral-900">Cost Ranges</h4>
          {product.costRanges && product.costRanges.length > 0 ? (
            <Card variant="bordered">
              <CardBody className="p-0">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Min Quantity</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Max Quantity</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Unit</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase text-neutral-500">Price per Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 bg-white">
                    {product.costRanges.map((range, idx) => (
                      <tr key={idx}>
                        <td className="whitespace-nowrap px-3 py-2 text-sm text-neutral-900">{range.minQuantity}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-sm text-neutral-900">{range.maxQuantity || 'Unlimited'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-sm text-neutral-900">{range.unit || product.unit || '-'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-sm text-neutral-900">
                          ${parseFloat(range.pricePerUnit).toFixed(2)} {range.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          ) : (
            <p className="text-sm text-neutral-500">No cost ranges defined</p>
          )}
        </div>

        <ProductQuoteHistoryPanel productId={product.id} />

        <div>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-neutral-900">Documents</h4>
              <p className="mt-1 text-xs text-neutral-500">{allDocuments.length} total documents</p>
            </div>
            {allowDocumentManagement && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowUploadForm(!showUploadForm)}
                leftIcon={<PlusIcon className="h-4 w-4" />}
              >
                Upload Document
              </Button>
            )}
          </div>

          {allowDocumentManagement && showUploadForm && (
            <Card variant="bordered" className="mb-4">
              <CardBody>
                <form onSubmit={handleUpload}>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Document Type</label>
                      <select
                        value={uploadData.fileType}
                        onChange={(e) =>
                          setUploadData((current) => ({
                            ...current,
                            fileType: e.target.value as 'COA' | 'MSDS' | 'OTHER',
                          }))
                        }
                        className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
                      >
                        <option value="COA">Certificate of Analysis (COA)</option>
                        <option value="MSDS">Material Safety Data Sheet (MSDS)</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700">File Name (optional)</label>
                      <input
                        type="text"
                        value={uploadData.fileName}
                        onChange={(e) => setUploadData((current) => ({ ...current, fileName: e.target.value }))}
                        placeholder="Custom file name"
                        className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700">Parent Document</label>
                      <select
                        value={uploadData.parentDocumentId}
                        onChange={(e) => setUploadData((current) => ({ ...current, parentDocumentId: e.target.value }))}
                        className="mt-1 block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
                      >
                        <option value="">Top-level document</option>
                        {parentOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-neutral-700">File</label>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        required
                        className="mt-1 block w-full text-sm text-neutral-500 file:mr-4 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-neutral-500">
                    Pick a parent document to attach this file as a child document, like a child COA under a main COA.
                  </p>

                  <div className="mt-4 flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowUploadForm(false);
                        setUploadData({ file: null, fileType: 'COA', fileName: '', parentDocumentId: '' });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary" isLoading={uploadingDoc} disabled={!uploadData.file}>
                      Upload
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-neutral-500">Loading documents...</span>
            </div>
          ) : documents.length === 0 ? (
            <p className="text-sm text-neutral-500">No documents uploaded</p>
          ) : (
            <div className="space-y-2">{renderDocuments(documents)}</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailModal;
